import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import fr from './locales/fr.json';

export const SUPPORTED_LANGUAGES = ['en', 'fr'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Snap an externally-supplied language tag onto one of ours, or null when it
 * isn't one.
 *
 * BMO hands us `language` on the advisor entry URL (spec §35), forwarded
 * through the ISAM login round-trip. **We do not control how they spell it**,
 * and the check this replaces was an exact `['en','fr'].includes(...)` — so
 * `fr-CA` was silently ignored and the advisor got English. That spelling is
 * not hypothetical: BMO's own pages live under `/fr-ca/`, so a link built from
 * their locale is the likeliest thing to arrive.
 *
 * Matches on the PRIMARY SUBTAG, case-insensitively, and tolerates both `-`
 * and `_` separators. Deliberately not a prefix test: "frisian" is not French.
 * Anything unrecognised returns null so the caller leaves the current language
 * alone rather than flipping to one nobody asked for.
 */
export function normalizeLanguage(raw: string | null | undefined): SupportedLanguage | null {
  if (!raw) return null;
  const primary = raw.trim().split(/[-_]/)[0]?.toLowerCase();
  if (!primary) return null;
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(primary)
    ? (primary as SupportedLanguage)
    : null;
}

/**
 * Query-string keys that carry a language hint, in priority order.
 *
 * `language` is the one BMO already sends: it is part of the advisor entry
 * contract (spec §35) and survives the ISAM login round-trip. `lang` is the
 * conventional spelling and costs one array entry to accept — **we do not
 * control the links**, they are built on BMO's side, and the failure mode of
 * guessing wrong is silent (a French visitor reading English), not loud.
 */
const URL_LANGUAGE_KEYS = ['language', 'lang'] as const;

/**
 * Read a language hint off the current URL.
 *
 * BMO's site lists the Smart Forms on separate English and French pages and
 * links straight in. Honouring the hint only on `/advisor/entry` — where it was
 * handled before — covers the SSO advisor and nobody else: a public visitor
 * (spec §22) never passes through that route, so they would land in whatever
 * language the browser happened to prefer, on a link that said otherwise.
 *
 * Exported for tests: i18next initialises once at module load, so the detector
 * cannot be re-run per case.
 */
export function lookupUrlLanguage(search?: string): SupportedLanguage | undefined {
  const query = search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const params = new URLSearchParams(query);
  for (const key of URL_LANGUAGE_KEYS) {
    const resolved = normalizeLanguage(params.get(key));
    if (resolved) return resolved;
  }
  return undefined;
}

// An instance, not the class, because a custom detector has to be registered on
// one — `.use(LanguageDetector)` would leave nowhere to call addDetector().
const languageDetector = new LanguageDetector();
languageDetector.addDetector({ name: 'urlLanguage', lookup: () => lookupUrlLanguage() });

void i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    interpolation: { escapeValue: false },
    detection: {
      // URL first, ahead of localStorage: an explicit link from BMO's French
      // page is a statement about THIS visit and has to beat a language this
      // browser happened to choose on some earlier one. Cached afterwards, so
      // the choice survives the navigation away from the entry URL.
      order: ['urlLanguage', 'localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

/**
 * Keep `<html lang>` in step with the active language (WCAG 2.1 — 3.1.1
 * Language of Page, Level A).
 *
 * index.html hardcodes `lang="en-CA"`. Nothing updated it, so every French
 * session served French content while telling assistive tech it was English —
 * screen readers then pronounce it with English phonetics, which is close to
 * unintelligible. A guaranteed Level A failure on a deliberately bilingual
 * product, and invisible to sighted testing.
 *
 * Region-qualified because the product is Canadian and the copy is written for
 * Canadian French / Canadian English.
 */
const HTML_LANG: Record<string, string> = { en: 'en-CA', fr: 'fr-CA' };

function syncDocumentLanguage(lng: string): void {
  if (typeof document === 'undefined') return;
  const base = lng.split('-')[0] ?? 'en';
  document.documentElement.lang = HTML_LANG[base] ?? base;
}

syncDocumentLanguage(i18n.language);
i18n.on('languageChanged', syncDocumentLanguage);

export default i18n;
